import { useState } from "react";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import { useKonaseemaGuide } from "@/hooks/usePublicGuide";

/**
 * Interactive season switcher for the Konaseema guide.
 * Visitors tap Winter / Summer / Monsoon to flip through the best-time-to-visit details.
 */
export default function KonaseemaSeasons() {
  const { seasons } = useKonaseemaGuide();
  const [activeIndex, setActiveIndex] = useState(0);
  const season = seasons[activeIndex] || seasons[0];

  if (!season) return null;

  return (
    <section id="best-season" className="sun-glaze bg-background-100 py-20 md:py-28">
      <div className="w-full px-4 md:px-8">
        <Reveal className="max-w-3xl mb-10 md:mb-14">
          <div className="flex items-center gap-3 mb-5">
            <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
            <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
              When to Visit
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-5xl leading-tight text-foreground-950 font-semibold">
            Best Season &amp; Travel Tips
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
            Konaseema changes character with every season. Tap a season to see what to expect,
            what's happening on the land, and when each experience shines the brightest.
          </p>
        </Reveal>

        <Reveal>
          <div
            role="tablist"
            aria-label="Choose a season to visit Konaseema"
            className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4"
          >
            {seasons.map((item, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  key={item.name}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveIndex(index)}
                  className={`text-left rounded-lg border px-5 py-4 md:px-6 md:py-5 transition-colors cursor-pointer ${
                    isActive
                      ? "bg-primary-900 border-primary-800"
                      : "bg-background-50 border-background-200 hover:border-accent-300"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`w-11 h-11 flex items-center justify-center rounded-full shrink-0 ${
                        isActive
                          ? "bg-accent-500 text-background-50"
                          : "bg-primary-100 text-primary-700"
                      }`}
                    >
                      <i className={`${item.icon} text-xl`}></i>
                    </span>
                    <div className="min-w-0">
                      <p
                        className={`font-heading text-lg font-semibold ${
                          isActive ? "text-background-50" : "text-foreground-950"
                        }`}
                      >
                        {item.name}
                      </p>
                      <p
                        className={`text-xs font-semibold uppercase tracking-wider mt-0.5 ${
                          isActive ? "text-accent-300" : "text-accent-600"
                        }`}
                      >
                        {item.months}
                      </p>
                    </div>
                    {item.best && (
                      <span className="ml-auto bg-accent-500 text-background-50 text-[10px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
                        Best
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </Reveal>

        <Reveal className="mt-6 md:mt-8">
          <div
            key={season.name}
            role="tabpanel"
            className="page-fade rounded-lg border border-background-200 bg-background-50 p-7 md:p-10"
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              <div className="lg:col-span-5">
                <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className={`${season.icon} text-3xl`}></i>
                </span>
                <h3 className="font-heading text-3xl md:text-4xl text-foreground-950 font-semibold mt-5">
                  {season.name}
                </h3>
                <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-accent-600 mt-2">
                  <i className="ri-calendar-line"></i>
                  {season.months}
                </p>
                {season.best && (
                  <span className="inline-flex items-center gap-1.5 bg-accent-500 text-background-50 text-xs font-semibold px-3 py-1.5 rounded-full mt-4">
                    <i className="ri-leaf-line"></i>
                    Best time to visit
                  </span>
                )}
              </div>

              <div className="lg:col-span-7 space-y-5">
                <div className="flex items-start gap-4 bg-background-100 rounded-lg border border-background-200 p-5">
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 shrink-0">
                    <i className="ri-temp-hot-line text-xl"></i>
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500">
                      Weather
                    </p>
                    <p className="text-sm md:text-base text-foreground-800 leading-relaxed mt-1">
                      {season.weather}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-background-100 rounded-lg border border-background-200 p-5">
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 shrink-0">
                    <i className="ri-leaf-line text-xl"></i>
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500">
                      On the land
                    </p>
                    <p className="text-sm md:text-base text-foreground-800 leading-relaxed mt-1">
                      {season.highlights}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-accent-50 rounded-lg border border-accent-200 p-5">
                  <span className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-500 text-background-50 shrink-0">
                    <i className="ri-lightbulb-line text-xl"></i>
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-accent-700">
                      Travel tip
                    </p>
                    <p className="text-sm md:text-base text-foreground-800 leading-relaxed mt-1">
                      {season.tip}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}